import { getFridgeId } from "@jidlomat/terminal-core/utils/browser/localStorage";
import {
	getLastRecoveryReloadAt,
	RECOVERY_RELOAD_STORM_WINDOW_MS,
	reloadForRecoveryNow,
} from "@jidlomat/terminal-core/utils/recoveryReload";
import * as Sentry from "@sentry/react";

// The steps of every recovery of the app that reloads the page: ask the origin whether it answers,
// ask for the recovery reload, and know when the next attempt may run. One copy of them, so that the
// recoveries cannot drift apart in how they treat an origin out of reach or the rate limit.

const ORIGIN_PROBE_TIMEOUT_MS = 4000;

/**
 * Whether the page could be fetched right now. The kiosk has no HTTP cache, so a reload while the
 * origin is out of reach cannot fetch the page either, and nothing of the app is left to recover.
 * Like the DNS ping of the Apollo client: no cache in between, a URL of its own, and a timeout.
 *
 * @param signal lets the caller give the probe up; a probe given up answers false and leaves no timer
 */
export const isOriginReachable = async (signal?: AbortSignal): Promise<boolean> => {
	if (signal?.aborted) {
		return false;
	}
	const controller = new AbortController();
	const abort = () => controller.abort();
	signal?.addEventListener("abort", abort, { once: true });
	const timeout = window.setTimeout(abort, ORIGIN_PROBE_TIMEOUT_MS);
	try {
		const response = await fetch(`${window.location.origin}${window.location.pathname}?probe=${Date.now()}`, {
			method: "HEAD",
			cache: "no-store",
			signal: controller.signal,
		});
		return response.ok;
	} catch {
		return false;
	} finally {
		window.clearTimeout(timeout);
		signal?.removeEventListener("abort", abort);
	}
};

/**
 * Asks for the recovery reload. False when it is held back, and when it cannot even be asked for.
 *
 * @param reason what the reload recovers from, for its log
 */
export const askForReload = (reason: string): boolean => {
	try {
		return reloadForRecoveryNow(getFridgeId() ?? "", reason);
	} catch (error) {
		// The rate limit lives in storage, and storage can throw. That must not end the handling of
		// the failure before a next attempt is armed.
		Sentry.captureException(error);
		return false;
	}
};

/**
 * How long until the rate limit of the recovery reload lets go: the rest of the window of the last
 * reload, and a whole window when there was none or it cannot be told. Never less, or a fetch that
 * keeps failing would spin; never more, or a clock that was set back would keep the tablet waiting.
 */
export const untilReloadAllowed = (): number => {
	try {
		const rest = RECOVERY_RELOAD_STORM_WINDOW_MS - (Date.now() - getLastRecoveryReloadAt());
		return rest > 0 ? Math.min(rest, RECOVERY_RELOAD_STORM_WINDOW_MS) : RECOVERY_RELOAD_STORM_WINDOW_MS;
	} catch {
		return RECOVERY_RELOAD_STORM_WINDOW_MS;
	}
};
